/* v6.35.8 — SuperAdmin Recent Wedges pattern-detector panel.
 *
 * Polls GET /api/bp-auditor/diagnostics/recent-wedges every 60s. Renders:
 *   - Total wedges in the window + source split (audit vs reverify)
 *   - Top-K buckets by last_current_op, with count + per-source breakdown
 *     + sample run_ids + first/last failed_at
 *   - Empty state when there are no recent wedges
 *
 * Operator use: spot systemic patterns ("every wedge clusters on
 * generating_gpt_image_1") so they know which call site to harden next.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AlertTriangle, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const t = localStorage.getItem('mosaic_session_token') || '';
  return { headers: { Authorization: `Bearer ${t}` } };
};

const fmtIso = (iso) => {
  if (!iso) return '—';
  try {
    return iso.slice(0, 19).replace('T', ' ').replace('Z', '');
  } catch {
    return iso;
  }
};

const BPRecentWedgesPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [days, setDays] = useState(7);

  const load = useCallback(async () => {
    try {
      const { data: resp } = await axios.get(
        `${API}/bp-auditor/diagnostics/recent-wedges?days=${days}`,
        authCfg(),
      );
      setData(resp);
    } catch (_) {
      // Silent — non-critical companion panel.
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  if (loading) {
    return (
      <div
        className="bg-white border border-gray-200 rounded-xl p-4 text-xs text-gray-500"
        data-testid="bp-recent-wedges-loading"
      >
        Loading recent-wedges pattern detector…
      </div>
    );
  }

  const total = data?.total_wedges || 0;
  const bySource = data?.by_source || {};
  const buckets = data?.buckets || [];

  return (
    <div
      className="bg-white border border-gray-200 rounded-xl p-4 space-y-3"
      data-testid="bp-recent-wedges-panel"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} className="text-orange-600" />
          <h3 className="text-sm font-semibold">Recent Wedges</h3>
          <Badge variant="outline" className="text-[10px]">v6.35.8</Badge>
          <span className="text-[11px] text-gray-500">
            last {days} days
          </span>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="text-[11px] border border-gray-200 rounded px-2 py-1 bg-white"
            data-testid="bp-recent-wedges-days-select"
          >
            <option value={1}>1 day</option>
            <option value={3}>3 days</option>
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
          </select>
          <Button
            variant="ghost"
            size="sm"
            onClick={load}
            data-testid="bp-recent-wedges-refresh"
            className="h-7 text-xs"
          >
            <RefreshCw size={12} className="mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Total + source split */}
      <div
        className="flex items-center gap-3 text-xs"
        data-testid="bp-recent-wedges-summary"
      >
        <span>
          <strong className={total === 0 ? 'text-emerald-700' : 'text-orange-700'}>
            {total}
          </strong>
          <span className="text-gray-500 ml-1">total wedge{total === 1 ? '' : 's'}</span>
        </span>
        {total > 0 && (
          <>
            <span className="text-gray-400">·</span>
            <span className="text-gray-600">
              audit <strong>{bySource.audit || 0}</strong>
            </span>
            <span className="text-gray-400">·</span>
            <span className="text-gray-600">
              reverify <strong>{bySource.reverify || 0}</strong>
            </span>
          </>
        )}
      </div>

      {/* Empty state */}
      {total === 0 && (
        <div
          className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2"
          data-testid="bp-recent-wedges-empty"
        >
          No wedges in the last {days} days — production is healthy.
        </div>
      )}

      {/* Buckets — top 10 */}
      {buckets.length > 0 && (
        <div data-testid="bp-recent-wedges-buckets">
          <div className="text-[11px] font-medium text-gray-700 mb-1">
            By last content-engine phase{' '}
            <span className="text-gray-400 font-normal">
              · top {Math.min(buckets.length, 10)}
            </span>
          </div>
          <div className="space-y-1">
            {buckets.slice(0, 10).map((b) => {
              const isOpen = !!expanded[b.key];
              return (
                <div
                  key={b.key}
                  className="bg-gray-50 border border-gray-100 rounded text-[11px]"
                  data-testid="bp-recent-wedges-bucket"
                  data-bucket-key={b.key}
                >
                  <button
                    type="button"
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-left hover:bg-gray-100"
                    onClick={() =>
                      setExpanded((prev) => ({ ...prev, [b.key]: !prev[b.key] }))
                    }
                    data-testid="bp-recent-wedges-bucket-toggle"
                  >
                    {isOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                    <span className="font-mono text-[10px] text-gray-700 flex-1 truncate">
                      {b.key}
                    </span>
                    <Badge className="bg-orange-100 text-orange-800 border-orange-300 text-[10px]">
                      {b.count}
                    </Badge>
                    <span className="text-[10px] text-gray-500">
                      {b.sources.audit > 0 && (
                        <span className="mr-1">A:{b.sources.audit}</span>
                      )}
                      {b.sources.reverify > 0 && (
                        <span>R:{b.sources.reverify}</span>
                      )}
                    </span>
                  </button>
                  {isOpen && (
                    <div
                      className="px-3 py-2 text-[10px] text-gray-600 border-t border-gray-200 space-y-1"
                      data-testid="bp-recent-wedges-bucket-detail"
                    >
                      <div>
                        First: <span className="font-mono">{fmtIso(b.first_failed_at)}</span>
                      </div>
                      <div>
                        Last: <span className="font-mono">{fmtIso(b.last_failed_at)}</span>
                      </div>
                      {b.sample_run_ids && b.sample_run_ids.length > 0 && (
                        <div className="pt-1">
                          <div className="font-medium text-gray-700 mb-0.5">Sample run ids:</div>
                          <ul className="space-y-0.5">
                            {b.sample_run_ids.map((rid) => (
                              <li key={rid} className="font-mono text-[10px]">
                                {rid}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default BPRecentWedgesPanel;
